/**
 * CompanySelector — toolbar company context switcher.
 *
 * For GLOBAL type (showGlobalOption=true): shows "Global" first + companies.
 * For COMPANY type: shows companies only.
 *
 * States:
 * - 0 companies (no showGlobalOption): static "Global" chip, click shows toast
 * - 0 companies + showGlobalOption: static "Global" chip (no dropdown needed)
 * - 1 company (no showGlobalOption): static chip with company name
 * - 1+ companies + showGlobalOption: clickable chip with Global + companies dropdown
 * - 2+ companies (no showGlobalOption): clickable chip with companies dropdown
 *
 * Chip shows Globe icon when activeCompanyId is 'global'/undefined, Building2 otherwise.
 *
 * @module components/toolbar/CompanySelector
 * @see FMPV-MVWC-E004-S002 I-001
 */
import React, { useState, useRef, useEffect } from 'react';
import { GlobeAltIcon, ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import { Building2 } from 'lucide-react';
import type { MasterPatternViewCompany } from '../../types/service.types';
import type { ToastCallback } from '../../hooks/useToast';
import { useToast } from '../../hooks/useToast';

export interface CompanySelectorProps {
  companies: MasterPatternViewCompany[];
  activeCompanyId?: string;
  onCompanyChange: (company: MasterPatternViewCompany) => void;
  toast?: ToastCallback;
  showInternalToasts?: boolean;
  /** When true, prepends a "Global" option to the dropdown (for GLOBAL master types). */
  showGlobalOption?: boolean;
  /**
   * Company ids the CURRENT record is enabled for (GLOBAL type only). When provided, each
   * company row shows a "Habilitado" / "No habilitado" indicator alongside its name/code —
   * same enable/disable data as the "Habilitar por compañía" modal, so switching context
   * from here tells you upfront whether the record actually has anything to show for that
   * company. Omit to leave the dropdown as it is today (no indicator).
   */
  assignedCompanyIds?: string[];
  /** When true, renders the chip as non-interactive (grayed out, no dropdown). */
  disabled?: boolean;
  /** Translation function override — falls back to the current Spanish text when omitted. */
  t?: (key: string, defaultValue?: string) => string;
}

const GLOBAL_SENTINEL: MasterPatternViewCompany = { id: 'global', name: 'Global', code: '' };

export function CompanySelector({
  companies,
  activeCompanyId,
  onCompanyChange,
  toast: toastProp,
  showInternalToasts = true,
  showGlobalOption = false,
  assignedCompanyIds,
  disabled = false,
  t,
}: CompanySelectorProps) {
  const tt = t ?? ((key: string, defaultValue?: string) => defaultValue ?? key);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { showWarning } = useToast(toastProp, showInternalToasts);

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  const isGlobalActive = !activeCompanyId || activeCompanyId === 'global';
  // Trigger azul de marca — mismos tokens que MasterCrud/CompanySelector.tsx (incluyendo dark:).
  const chipClass =
    'inline-flex items-center gap-1.5 bg-[#eff8ff] dark:bg-primary-900/20 border border-[#93d1fd] dark:border-primary-800 rounded-md px-3 py-1.5 text-xs font-semibold text-[#1355b7] dark:text-primary-300 leading-none cursor-pointer hover:bg-[#dbeefe] dark:hover:bg-primary-900/40 hover:border-[#3c9bf6] transition-colors select-none';
  const chipDisabledClass =
    'inline-flex items-center gap-1.5 bg-background-secondary dark:bg-dark-bg-secondary border border-border-primary dark:border-dark-border-primary rounded-md px-3 py-1.5 text-xs font-semibold text-content-tertiary dark:text-dark-content-tertiary leading-none cursor-not-allowed select-none opacity-60';

  // No companies and no global option — static chip with toast
  if ((!companies || companies.length === 0) && !showGlobalOption) {
    return (
      <button
        type="button"
        data-testid="company-chip-global"
        className={disabled ? chipDisabledClass : chipClass}
        onClick={disabled ? undefined : () => showWarning(tt('noCompaniesConfigured', 'No hay compañías configuradas para este maestro.'))}
        disabled={disabled}
      >
        <GlobeAltIcon className="w-3.5 h-3.5 flex-shrink-0" />
        <span>{tt('global', 'Global')}</span>
        <ChevronDownIcon className="w-3 h-3 flex-shrink-0" />
      </button>
    );
  }

  // Single company, no global option — static non-interactive chip
  if (companies.length === 1 && !showGlobalOption) {
    return (
      <div
        data-testid="company-chip"
        className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold leading-none select-none ${
          disabled
            ? 'bg-background-secondary dark:bg-dark-bg-secondary border border-border-primary dark:border-dark-border-primary text-content-tertiary dark:text-dark-content-tertiary opacity-60'
            : 'bg-[#eff8ff] dark:bg-primary-900/20 border border-[#93d1fd] dark:border-primary-800 text-[#1355b7] dark:text-primary-300'
        }`}
      >
        <Building2 className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="max-w-[160px] truncate">{companies[0].name}</span>
      </div>
    );
  }

  // Dropdown: Global + companies (showGlobalOption) OR companies only
  const activeCompany = companies.find((c) => c.id === activeCompanyId);
  const activeLabel = isGlobalActive ? tt('global', 'Global') : (activeCompany?.name ?? companies[0]?.name ?? tt('global', 'Global'));
  const ActiveIcon = isGlobalActive ? GlobeAltIcon : Building2;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        data-testid="company-dropdown-trigger"
        className={disabled ? chipDisabledClass : chipClass}
        onClick={disabled ? undefined : () => setDropdownOpen((v) => !v)}
        disabled={disabled}
      >
        <ActiveIcon className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="max-w-[160px] truncate">{activeLabel}</span>
        <ChevronDownIcon className={`w-3 h-3 flex-shrink-0 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
      </button>

      {dropdownOpen && (
        <div
          data-testid="company-dropdown"
          // Wider when the enabled/disabled indicator is shown (assignedCompanyIds provided)
          // — same breakpoint MasterCrud's own CompanySelector uses for its companyEnabledMap
          // case, so the extra text doesn't cramp against the name/code.
          className={`absolute left-0 top-full mt-1 z-50 ${
            assignedCompanyIds !== undefined ? 'min-w-[360px] max-w-[440px]' : 'min-w-[200px]'
          } bg-bg-primary dark:bg-dark-bg-primary border border-border-primary dark:border-dark-border-primary rounded-lg shadow-md py-1`}
        >
          {/* Global option — first, for GLOBAL master types */}
          {showGlobalOption && (
            <>
              <button
                type="button"
                data-testid="company-option-global"
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-content-primary dark:text-dark-content-primary hover:bg-background-secondary dark:hover:bg-dark-bg-secondary transition-colors"
                onClick={() => {
                  onCompanyChange(GLOBAL_SENTINEL);
                  setDropdownOpen(false);
                }}
              >
                {isGlobalActive
                  ? <CheckIcon className="w-3 h-3 text-[#0e79fd] flex-shrink-0" />
                  : <span className="w-3 flex-shrink-0" />}
                <GlobeAltIcon className="w-3 h-3 flex-shrink-0 text-[#1355b7] dark:text-primary-300" />
                <span className="font-medium">{tt('global', 'Global')}</span>
              </button>
              {companies.length > 0 && (
                <hr className="my-1 border-border-primary dark:border-dark-border-primary" />
              )}
            </>
          )}

          {/* Companies */}
          {companies.map((company) => {
            const isActive = company.id === activeCompanyId;
            const isAssigned = assignedCompanyIds?.includes(company.id);
            return (
              <button
                key={company.id}
                type="button"
                data-testid={`company-option-${company.id}`}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-content-primary dark:text-dark-content-primary hover:bg-background-secondary dark:hover:bg-dark-bg-secondary transition-colors"
                onClick={() => {
                  onCompanyChange(company);
                  setDropdownOpen(false);
                }}
              >
                {isActive
                  ? <CheckIcon className="w-3 h-3 text-[#0e79fd] flex-shrink-0" />
                  : <span className="w-3 flex-shrink-0" />}
                <Building2 className="w-3 h-3 flex-shrink-0 text-[#1355b7] dark:text-primary-300" />
                <span className="font-medium truncate">{company.name}</span>
                <span className="flex items-center gap-2 ml-auto flex-shrink-0">
                  <span className="text-content-secondary dark:text-dark-content-secondary">{company.code}</span>
                  {/* Plain icon + text, no badge background — matches MasterCrud's own
                      CompanySelector.tsx (companyEnabledMap) exactly, not a colored pill. */}
                  {assignedCompanyIds !== undefined && (
                    isAssigned ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#4d7c0f] dark:text-lime-400">
                        <CheckIcon className="w-3 h-3" />
                        {tt('enabled', 'Habilitado')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-content-secondary dark:text-dark-content-secondary">
                        <span className="inline-block w-3 h-3 rounded-full border border-current" />
                        {tt('notEnabled', 'No habilitado')}
                      </span>
                    )
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
